import { EventBus } from '@servicetitan/web-components';
import IWebPhoneService from './IWebPhoneService';
import handleWebPhoneDialerStateUpdate from './events/handleWebPhoneDialerStateUpdate';
import { DialerState, WebphonePhoneStateEvent } from './types';
import IAppLogger from '@app/lib/IAppLogger';
import handlePhoneStateUpdate from './events/handlePhoneStateUpdate';
import handleRegistrationOverwritten from './events/handleRegistrationOverwritten';

// events are from https://github.com/servicetitan/telecom-webphone/blob/master/packages/application/src/utils/eventNames.ts
export const phoneCommandNames = {
  mute: 'mute',
  unmute: 'unmute',
  hold: 'hold',
  unhold: 'unhold',
  hangup: 'hangup',
  answer: 'answer',
  cancel: 'cancel',
  dial: 'dialer:dial',
  clear: 'dialer:clear',
  backspace: 'dialer:backspace',
  autoAction: 'dialer:autoAction',
  callNumber: 'dialer:callNumber',
  disconnect: 'disconnect',
};

const webPhoneEventNames = {
  callState: 'webphone:call-state',
  dialerState: 'webphone:dialer-state',
  phoneState: 'webphone:phone-state',
  registrationOverwritten: 'webphone:registration-overwritten',
};

export const outgoingEventNames = {
  hostCommand: 'webphone:host-command',
};

export const webPhoneLastTriggeringEventNames = {
  callStarted: 'call.created',
  callEnded: 'call.ringing.stopped',
};

/**
 * Handles the communication with Telecom's WebPhone.
 */
class WebPhoneService implements IWebPhoneService {
  private eventBus: EventBus | null = null;
  private logger: IAppLogger | null = null;

  /**
   * Answers an incoming call.
   */
  public answer(callId?: string): void {
    this.emitCommand('answer', { callId });
  }

  /**
   * Rejects a call.
   */
  public cancel(callId?: string): void {
    this.emitCommand(phoneCommandNames.cancel, { callId });
  }

  /**
   * Hangs up the current call.
   */
  public hangup(callId?: string): void {
    this.emitCommand('hangup', { callId });
  }

  /**
   * Puts the current call on hold.
   */
  public hold(): void {
    this.emitCommand(phoneCommandNames.hold, {});
  }

  /**
   * Resumes a held call.
   */
  public unhold(callId?: string): void {
    this.emitCommand('unhold', { callId });
  }

  /**
   * Mutes the microphone during a call.
   */
  public mute(): void {
    this.emitCommand(phoneCommandNames.mute, {});
  }

  /**
   * Unmutes the microphone during a call.
   */
  public unmute(): void {
    this.emitCommand(phoneCommandNames.unmute, {});
  }

  /**
   * Enters a phone number to dial pad
   * @param number - The phone number to dial.
   */
  public dial(number: string): void {
    this.emitCommand(phoneCommandNames.dial, { number });
  }

  /**
   * Clears the entered digits in the dial pad
   */
  public clear(): void {
    this.emitCommand(phoneCommandNames.clear, {});
  }

  /**
   * Deletes the last entered digit in the dial pad.
   */
  public backspace(): void {
    this.emitCommand(phoneCommandNames.backspace, {});
  }

  /**
   * Performs an automatic action on the phone.
   */
  public autoAction(): void {
    this.emitCommand(phoneCommandNames.autoAction, {});
  }

  /**
   * Calls a phone number.
   * @param number - The phone number to call.
   */
  public callNumber(number: string): void {
    this.emitCommand(phoneCommandNames.callNumber, { number });
  }

  public disconnect(): void {
    this.emitCommand(phoneCommandNames.disconnect, {});
  }

  public warmTransfer(targetCallId: string): void {
    this.emitCommand('transfer:warm', { targetCallId });
  }

  /**
   * Initializes the WebPhoneService with the specified event bus.
   * @param eventBus - The event bus to use for communication.
   */
  public initialize(eventBus: EventBus, logger: IAppLogger): void {
    this.eventBus = eventBus;
    this.logger = logger;
    this.registerEventHandlers();
  }

  private registerEventHandlers() {
    // dialerState update
    this.eventBus?.on(
      webPhoneEventNames.dialerState,
      (dialerState: DialerState) => {
        this.logger?.debug('Dialer state updated', dialerState);
        handleWebPhoneDialerStateUpdate(this, dialerState);
      },
    );

    // phoneState update
    this.eventBus?.on<WebphonePhoneStateEvent>(
      webPhoneEventNames.phoneState,
      (phoneState) => {
        this.logger?.debug('Phone state updated', phoneState);
        handlePhoneStateUpdate(this, phoneState, this.logger!);
      },
    );

    // registration overwritten
    this.eventBus?.on(webPhoneEventNames.registrationOverwritten, () => {
      this.logger?.debug('Registration Overwritten');
      handleRegistrationOverwritten();
    });
  }

  private emitCommand(command: string, args: any): void {
    this.eventBus?.emit(outgoingEventNames.hostCommand, {
      command: command,
      args: args,
    });
  }
}

export default new WebPhoneService();
